import React from "react";
import {
  Modal,
  Typography,
  Table,
  Space,
  Tag,
  Divider,
} from "antd";
import {
  FileTextOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CarOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Title, Text } = Typography;

const InvoicePreview = ({ visible, onClose, invoiceData }) => {
  if (!invoiceData) return null;

  const formatNumber = (number) =>
    `₹${new Intl.NumberFormat("en-IN").format(number || 0)}`;

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "cancelled":
        return "error";
      case "replace":
        return "processing";
      default:
        return "default";
    }
  };

  // Calculate totals similar to the PDF generation
  const calculateProductTotals = () => {
    let totalDiscount = 0;
    let totalDiscountedAmount = 0;

    invoiceData.products.forEach((product) => {
      let productDiscount = 0;
      let productAmountWithDiscount = product.salePrice;

      if (product?.productDiscount?.type === "flat") {
        productDiscount = product.productDiscount.value;
        productAmountWithDiscount = product.salePrice - productDiscount;
      } else if (product?.productDiscount?.type === "percentage") {
        productDiscount = (product.salePrice * product.productDiscount.value) / 100;
        productAmountWithDiscount = product.salePrice - productDiscount;
      }

      totalDiscount += productDiscount;
      totalDiscountedAmount += productAmountWithDiscount;
    });

    return { totalDiscount, totalDiscountedAmount };
  };

  const { totalDiscount, totalDiscountedAmount } = calculateProductTotals();

  const productColumns = [
    {
      title: "Sr. No",
      key: "srNo",
      width: "5%",
      render: (_, record, index) => index + 1,
      align: "center",
    },
    {
      title: "DESCRIPTION",
      key: "description",
      width: "35%",
      render: (_, product) => {
        const productName = product.productId?.specifications?.name || "N/A";
        const barcode = product.productId?.barcode?.code || "";
        return `${productName} (${barcode})`;
      },
    },
    {
      title: "QUANTITY",
      dataIndex: "quantity",
      key: "quantity",
      width: "10%",
      render: (value) => value || 0,
      align: "center",
    },
    {
      title: "BASE PRICE",
      key: "basePrice",
      width: "10%",
      render: (_, product) => {
        const gstValue = product.taxes[0]?.percentage || 0;
        const gstAmount = ((product?.salePrice * gstValue) / 100).toFixed(2);
        const price = (product?.salePrice - gstAmount).toFixed(2);
        return formatNumber(price);
      },
      align: "center",
    },
    {
      title: "DISCOUNT (TYPE)",
      key: "discount",
      width: "10%",
      render: (_, product) => {
        const discountValue = product?.productDiscount?.value || 0;
        const discountType = product?.productDiscount?.type === "flat" ? "/-" : "%";
        return `${discountValue} ${discountType}`;
      },
      align: "center",
    },
    {
      title: "GST(%)",
      key: "gst",
      width: "10%",
      render: (_, product) => {
        const gstValue = product.taxes[0]?.percentage || 0;
        return `${gstValue}%`;
      },
      align: "center",
    },
    {
      title: "AMOUNT",
      dataIndex: "totalPrice",
      key: "amount",
      width: "15%",
      render: (value) => formatNumber(value),
      align: "center",
    },
  ];

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <FileTextOutlined style={{ color: "#1890ff" }} />
          <span>Invoice Preview</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      style={{ top: 10 }}
      bodyStyle={{ maxHeight: "90vh", overflow: "auto", padding: "20px" }}
    >
      <div style={{ 
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#fff",
        minHeight: "800px"
      }}>
        {/* Store Logo and Header */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "flex-start",
          marginBottom: "30px"
        }}>
          {/* Store Information */}
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: "16px", 
              fontWeight: "bold", 
              marginBottom: "10px",
              color: "#000"
            }}>
              {invoiceData.storeId?.billingHeaderName || invoiceData.storeId?.name || "Store Name"}
            </div>
            <div style={{ fontSize: "12px", lineHeight: "1.4", color: "#333" }}>
              <div>{invoiceData.storeId?.address || ""}</div>
              {invoiceData.storeId?.state && (
                <div>State: {invoiceData.storeId.state}</div>
              )}
              {invoiceData.storeId?.stateCode && (
                <span style={{ marginLeft: "10px" }}>StateCode: {invoiceData.storeId.stateCode}</span>
              )}
              {invoiceData.storeId?.pan && (
                <div>Pan: {invoiceData.storeId.pan}</div>
              )}
              <div>Phone: +91 {invoiceData.storeId?.phone || "Phone Number"}</div>
              <div>Email: {invoiceData.storeId?.email || "rhinotraders14@gmail.com"}</div>
              <div style={{ fontWeight: "bold" }}>
                GSTIN/UIN: {invoiceData.storeId?.gstNo || "-"}
              </div>
            </div>
          </div>

          {/* Invoice Title and Details */}
          <div style={{ textAlign: "right", flex: 1 }}>
            <div style={{ 
              fontSize: "28px", 
              fontWeight: "bold", 
              color: "#7b8dc5",
              marginBottom: "20px"
            }}>
              Invoice
            </div>
            
            {/* Invoice Details Table */}
            <div style={{ 
              border: "1px solid #a0a0a0",
              backgroundColor: "#d9e1f2",
              display: "inline-block",
              marginLeft: "auto"
            }}>
                
              <table style={{ borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr>
                    <th style={{ 
                      padding: "8px 12px", 
                      backgroundColor: "#d9e1f2", 
                      border: "1px solid #a0a0a0",
                      fontWeight: "bold",
                      fontSize: "12px"
                    }}>
                      Invoice No
                    </th>
                    <th style={{ 
                      padding: "8px 12px", 
                      backgroundColor: "#d9e1f2", 
                      border: "1px solid #a0a0a0",
                      fontWeight: "bold",
                      fontSize: "12px"
                    }}>
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ 
                      padding: "8px 12px", 
                      border: "1px solid #a0a0a0",
                      fontSize: "12px",
                      textAlign: "center"
                    }}>
                      {invoiceData.invoiceId || "N/A"}
                    </td>
                    <td style={{ 
                      padding: "8px 12px", 
                      border: "1px solid #a0a0a0",
                      fontSize: "12px",
                      textAlign: "center"
                    }}>
                      {invoiceData.orderDate ? moment(invoiceData.orderDate).format("YYYY-MM-DD") : "N/A"}
                    </td>
                  </tr>
                </tbody>
              </table>

            </div>
          </div>
        </div>

        {/* Customer and Financer Details */}
        <div style={{ 
          display: "flex", 
          gap: "20px", 
          marginBottom: "30px"
        }}>
          {/* Customer Details */}
          <div style={{ 
            flex: 1,
            border: "1px solid #a0a0a0",
            backgroundColor: "#d9e1f2"
          }}>
            <div style={{ 
              padding: "8px 12px", 
              backgroundColor: "#d9e1f2", 
              borderBottom: "1px solid #a0a0a0",
              fontWeight: "bold",
              fontSize: "12px"
            }}>
              Bill To
            </div>
            <div style={{ padding: "8px", fontSize: "11px", lineHeight: "1.4" }}>
              <div><strong>Name:</strong> {invoiceData.customerId?.name || "N/A"}</div>
              <div><strong>Phone:</strong> {invoiceData.customerId?.phone || "N/A"}</div>
              <div><strong>Address:</strong> {invoiceData.customerId?.address 
                ? `${invoiceData.customerId.address.street || "-"}, ${invoiceData.customerId.address.city || "-"}, ${invoiceData.customerId.address.state || "-"}, ${invoiceData.customerId.address.pincode || "-"}`
                : "N/A"}</div>
              <div><strong>GSTIN:</strong> {invoiceData.customerId?.gstNumber || "-"}</div>
            </div>
          </div>

          {/* Financer Details (if exists) */}
          {(invoiceData.finance || (invoiceData.productFinanceDetails && invoiceData.productFinanceDetails.length > 0)) && (
            <div style={{ 
              flex: 1,
              border: "1px solid #a0a0a0"
            }}>
              <div style={{ 
                padding: "8px 12px", 
                backgroundColor: "#d9e1f2", 
                borderBottom: "1px solid #a0a0a0",
                fontWeight: "bold",
                fontSize: "12px"
              }}>
                Financer
              </div>
              <div style={{ padding: "8px", fontSize: "11px", lineHeight: "1.4" }}>
                {invoiceData.finance && (
                  <div><strong>Financer:</strong> {invoiceData.finance.replace(/([a-z])([A-Z])/g, "$1 $2").split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ")}</div>
                )}
                {invoiceData.productFinanceDetails && invoiceData.productFinanceDetails.slice(0, 3).map((detail, index) => (
                  <div key={index}><strong>{detail.key}:</strong> {detail.value || "N/A"}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Product Table */}
        <div style={{ marginBottom: "30px" }}>
          <Table
            columns={productColumns}
            dataSource={invoiceData.products || []}
            rowKey={(record, index) => index}
            pagination={false}
            size="small"
            bordered
            style={{ fontSize: "12px" }}
            components={{
              header: {
                cell: (props) => (
                  <th style={{ 
                    backgroundColor: "#d9e1f2", 
                    border: "1px solid #a0a0a0",
                    padding: "8px 4px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    textAlign: "center"
                  }}>
                    {props.children}
                  </th>
                )
              },
              body: {
                cell: (props) => (
                  <td style={{ 
                    border: "1px solid #a0a0a0",
                    padding: "8px 4px",
                    fontSize: "11px",
                    textAlign: "center"
                  }}>
                    {props.children}
                  </td>
                )
              }
            }}
          />
        </div>

        {/* Totals Section */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          marginTop: "20px"
        }}>
          {/* Payment Details */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: "12px", lineHeight: "1.8" }}>
              <div><strong>Payment Mode:</strong> • {invoiceData.paymentMethod?.toUpperCase() || "N/A"}</div>
              <div><strong>Paid Amount:</strong> {formatNumber(invoiceData.paidAmount || 0)}</div>
              <div><strong>Balance Amount:</strong> {formatNumber(invoiceData.balanceAmount || 0)}</div>
            </div>
          </div>

          {/* Amount Details */}
          <div style={{ textAlign: "right", flex: 1 }}>
            <div style={{ fontSize: "12px", lineHeight: "1.8" }}>
              <div><strong>Tax (SGST + CGST):</strong> {formatNumber(invoiceData.totalAmount - invoiceData.totalAmountWithoutTax)} ({formatNumber((invoiceData.totalAmount - invoiceData.totalAmountWithoutTax) / 2)} + {formatNumber((invoiceData.totalAmount - invoiceData.totalAmountWithoutTax) / 2)})</div>
              <div><strong>Total Amount (without tax):</strong> {formatNumber(invoiceData.totalAmountWithoutTax || 0)}</div>
              <div><strong>Total Amount:</strong> {formatNumber(invoiceData.totalAmount)}</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          marginTop: "40px", 
          textAlign: "center", 
          fontSize: "10px", 
          color: "#666",
          borderTop: "1px solid #ddd",
          paddingTop: "20px"
        }}>
          <div>Rhinoceros Traders Pvt. Ltd.</div>
        </div>
      </div>
    </Modal>
  );
};

export default InvoicePreview;
